import React from 'react';
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Typography,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { useFeature } from '@vp/core-ui-components';
import {
  CancellationType,
  getTranslationKeyFromCancellationType,
} from '../../constants/cancellationType';
import useSiteData from '../../hooks/useSiteData';
import { EmbeddableLinkCreator } from './EmbeddableLinkCreator'; // get translated cancellation reason

type ItemStatusTooltipProps = {
  detail: CancellationType;
};

export const ItemStatusTooltip = ({ detail }: ItemStatusTooltipProps) => {
  const [cancellationReasons] = useTranslations(['cancellationReasons']);
  const itemStatusTooltipEnabled: boolean = useFeature(
    'enableItemStatusTooltip'
  );
  const cancellationReasonText: string = cancellationReasons.cancellationReason;
  const { externalAppNavigation, embroideryUrl } = useSiteData();
  const navToTermsAndConditions = externalAppNavigation.termsAndConditions;

  const TermsAndConditionsLink = EmbeddableLinkCreator({
    href: navToTermsAndConditions,
    tabIndex: 0,
  });

  const EmbroideryGuidelinesLink = EmbeddableLinkCreator({
    href: embroideryUrl,
    tabIndex: 0,
  });

  // get translatedReason for CancellationType
  const translationKey: string = getTranslationKeyFromCancellationType(detail);
  const translatedReason: string = cancellationReasons[translationKey] || '';

  // verify feature is enabled and StateQuantity `detail` property matches one of ODUI's CancellationTypes (with translated reasons)
  const shouldShowCancellationTooltip: boolean =
    itemStatusTooltipEnabled &&
    detail in CancellationType &&
    translatedReason !== '';

  return shouldShowCancellationTooltip ? (
    <Popover ml={1}>
      <PopoverLauncher>
        {/* minimum click target size must be 24px x 24px: https://www.w3.org/WAI/WCAG22/Understanding/target-size-minimum.html */}
        <Button
          skin="unstyled"
          style={{ minWidth: 24, minHeight: 24 }}
          aria-label={cancellationReasonText}
        >
          <Icon iconType="info" skin="error" alt={cancellationReasonText} />
        </Button>
      </PopoverLauncher>
      <PopoverContent
        labelText={`${cancellationReasonText}: ${translatedReason}`}
      >
        <Typography fontWeight="bold">{cancellationReasonText}: </Typography>
        <Typography>
          <ReplaceTokens
            tokenizedString={translatedReason}
            substitutions={{
              TermsAndConditionsLink,
              EmbroideryGuidelinesLink,
            }}
          />
        </Typography>
      </PopoverContent>
    </Popover>
  ) : null;
};
